import {
  CSSVariablesResolver,
  MantineThemeOverride,
  createTheme,
} from '@mantine/core'

import { idgThemeConfig } from '../themes/idgTheme'
import { ThemeType } from '../ui.types'

export interface ThemeConfig {
  name: ThemeType
  type: string
  version: string
  colors: Record<string, string[]>
  cssVariables: Record<string, string>
  components?: Record<string, any>
  mantineConfig: {
    primaryColor: string
    primaryShade: { light: number; dark: number }
    fontFamily: string
    defaultRadius?: string
    radius?: Record<string, string>
    white?: string
    black?: string
    headings?: {
      fontFamily: string
    }
    [key: string]: any
  }
}

export const themeConfigs: Record<ThemeType, ThemeConfig> = {
  [ThemeType.IDG]: idgThemeConfig,
}

export const createCssVariablesResolver = (
  config: ThemeConfig,
): CSSVariablesResolver => {
  return () => {
    const cssVariables: Record<string, string> = {}

    Object.entries(config.cssVariables).forEach(([key, value]) => {
      cssVariables[key] = value
    })

    return {
      variables: {
        ...cssVariables,
      },
      light: {},
      dark: {},
    }
  }
}

export const applyCustomCssVariables = (config: ThemeConfig): void => {
  const cssVariables = Object.entries(config.cssVariables)
    .map(([key, value]) => `--${key}: ${value};`)
    .join('\n')

  let styleElement = document.getElementById('theme-custom-variables')

  if (!styleElement) {
    styleElement = document.createElement('style')
    styleElement.id = 'theme-custom-variables'
    document.head.appendChild(styleElement)
  }

  styleElement.textContent = `:root {\n${cssVariables}\n}\n\nbody {\n  color: ${
    config.cssVariables['body-color'] || 'inherit'
  };\n}`
}

export const createMantineTheme = (
  themeType: ThemeType,
): {
  theme: MantineThemeOverride
  cssVariablesResolver: CSSVariablesResolver
} => {
  let config = themeConfigs[themeType]

  if (!config) {
    console.error(new Error(`Theme configuration for ${themeType} not found`))

    config = themeConfigs[ThemeType.IDG]
  }

  const theme = createTheme({
    colors: config.colors,
    ...config.mantineConfig,
    components: config.components,
  } as any)

  return {
    theme,
    cssVariablesResolver: createCssVariablesResolver(config),
  }
}

export const applyThemeAttribute = (themeType: ThemeType): void => {
  document.body.setAttribute('data-theme', themeType)
}
